import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { WA_LINK } from "../lib/content";
import SmoobuWidget from "../components/SmoobuWidget";
import BookingForm from "../components/BookingForm";

const SMOOBU_ACCOUNT_ID = process.env.REACT_APP_SMOOBU_ACCOUNT_ID || "";

export default function Booking() {
  const { lang } = useI18n();
  const [params] = useSearchParams();
  const [success, setSuccess] = useState(false);

  if (success) return <BookingSuccess />;

  return (
    <section className="py-16 md:py-24" data-testid="booking-page">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-12">
        <BookingIntro />

        {SMOOBU_ACCOUNT_ID ? (
          <div className="md:col-span-7" data-testid="booking-smoobu-wrapper">
            <SmoobuWidget accountId={SMOOBU_ACCOUNT_ID} language={lang} />
          </div>
        ) : (
          <BookingForm
            initialRoom={params.get("room") || ""}
            onSuccess={() => setSuccess(true)}
          />
        )}
      </div>
    </section>
  );
}

function BookingIntro() {
  const { t } = useI18n();

  return (
    <div className="md:col-span-5">
      <div className="overline">{t("booking.overline")}</div>
      <h1 className="mt-5 text-5xl md:text-6xl leading-[1.05]">
        {t("booking.title")}
      </h1>
      <p className="mt-6 text-[#5C5042] text-lg">{t("booking.subtitle")}</p>

      {/* Direct booking -15% incentive */}
      <div
        className="mt-8 bg-[#2C241B] text-[#F9F6F0] p-6"
        data-testid="booking-promo-card"
      >
        <div className="flex items-baseline gap-3">
          <span className="font-serif-display text-4xl leading-none">-15%</span>
          <span className="overline text-[#F9F6F0]/90">
            {t("promo.badge_title")}
          </span>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-[#F9F6F0]/85">
          {t("promo.badge_body")}
        </p>
      </div>

      {/* Smoobu side note — adapts based on whether widget is configured */}
      <div
        data-testid="smoobu-side-note"
        className={`mt-12 p-6 ${
          SMOOBU_ACCOUNT_ID
            ? "bg-[#EFEAE1]"
            : "border border-dashed border-[#6B4A2B]/30 bg-[#EFEAE1]/50"
        }`}
      >
        <div className="overline text-[#6B4A2B]/80">Smoobu</div>
        <p className="mt-3 text-sm text-[#5C5042] leading-relaxed">
          {SMOOBU_ACCOUNT_ID
            ? t("booking.smoobu_live_note")
            : t("booking.smoobu_note")}
        </p>
      </div>
    </div>
  );
}

function BookingSuccess() {
  const { t, lang } = useI18n();

  return (
    <section className="py-24 md:py-32" data-testid="booking-success">
      <div className="max-w-2xl mx-auto px-5 md:px-8 text-center">
        <div className="mx-auto h-16 w-16 border border-[#4F7942] text-[#4F7942] flex items-center justify-center">
          <Check size={28} strokeWidth={1.4} />
        </div>
        <div className="mt-8 overline">{t("booking.overline")}</div>
        <h1 className="mt-4 text-4xl md:text-5xl leading-[1.1]">
          {t("booking.success_title")}
        </h1>
        <p className="mt-6 text-[#5C5042] text-lg">{t("booking.success_body")}</p>
        <div className="mt-10 flex gap-4 justify-center flex-wrap">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
            style={{ background: "#25D366" }}
            data-testid="booking-success-whatsapp"
          >
            WhatsApp <ArrowUpRight size={14} />
          </a>
          <Link to="/" className="btn-outline" data-testid="booking-success-home">
            {lang === "fr" ? "Accueil" : "Home"}
          </Link>
        </div>
      </div>
    </section>
  );
}
